import React, { createRef, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Box, Grid } from "@mui/material";
import { Formik, setIn } from "formik";
import { FormattedMessage, useIntl } from "react-intl";
import { InputFieldFactory } from "app/components/FormikField";
import { actions as InsurersActions, selectors as insurersSelector } from "app/Domain/Insurers/Ducks/Insurers.duck";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty, isNil } from "app/utils";
import CollapsableSection from "app/components/CollapsableSection";
import { actions as AppActions } from "app/Domain/App/Ducks/App.duck";
import { mapSettingsField, SettingsFieldGroup } from "app/components/FormikField/Factory/SettingsFieldFactory";
import { onboardingService } from "../../../../Connections/Services";
import { formatDateTime } from "app/utils/formatter/dateTimeFormatter";
import { TranslationKeys } from "../../../../../translations";
import Typography from "@mui/material/Typography";
import { FormLabelStyled } from "../../../Pages/EmployerAddPage/EmployerAddPage.styles";
import useZwitserlevenSettings from "./useZwitserlevenSettings";
import useRelatedProducts from "./useRelatedProducts";
const ConnectionInfoStep = forwardRef(({ onSubmit, stepState, connection, loading = false }, ref) => {
  const parseConnectionDate = (dateValue) => dateValue !== void 0 && dateValue !== null ? new Date(dateValue) : null;
  const connectionLatestRun = connection ? parseConnectionDate(connection.latestRun) : null;
  const connectionNextRun = connection ? parseConnectionDate(connection.nextRun) : null;
  const formRef = createRef();
  const intl = useIntl();
  const dispatch = useDispatch();
  const [initialLoading, setInitialLoading] = useState(true);
  const [credentialSettingsLoading, setCredentialSettingsLoading] = useState(
    stepState.state.credentialSettingsLoading ?? false
  );
  const [credentialSettings, setCredentialSettings] = useState(stepState.state.credentialSettings ?? null);
  const [selectedProductEntityId, setSelectedProductEntityId] = useState(
    stepState.state.selectedProductEntityId ?? (connection?.product && connection?.product["@id"]) ?? null
  );
  const [selectedProduct, setSelectedProduct] = useState(
    stepState.state.selectedProduct ?? connection?.product ?? null
  );
  const [insurerId, setInsurerId] = useState(
    stepState.state.insurerId ?? connection?.product?.insurer?.insurerId ?? null
  );
  const [connectionName, setConnectionName] = useState(
    stepState.state.connectionName ?? connection?.connectionName ?? null
  );
  const [disabled, setDisabled] = useState(stepState.state.disabled ?? connection?.disabled ?? false);
  const { loading: insurersLoading, items: insurers } = useSelector(
    (state) => insurersSelector.selectInsurers(state.InsurersReducer)
  );
  const { applyZwitserlevenSettings } = useZwitserlevenSettings(selectedProduct);
  const selectedInsurer = useMemo(() => {
    return (insurers || []).find((insurer) => insurer.insurerId === insurerId) ?? null;
  }, [insurers, insurerId]);
  const products = useMemo(() => {
    return selectedInsurer?.products ?? [];
  }, [selectedInsurer]);
  const {
    selectedProductIds: productTypes,
    selectorItems: productTypesSelectorItems,
    handleChange: changeProductTypes,
    handleProductChange: onRelatedProductChange,
    clearSelection: clearRelatedProducts,
    resolveSubmitValues: resolveRelatedProductsSubmitValues
  } = useRelatedProducts({ connection, products, selectedProduct, stepState });
  const showProductTypesSelector = useMemo(() => {
    return selectedInsurer?.multipleProductType === true;
  }, [selectedInsurer]);
  const insurerFieldLoading = loading || initialLoading || insurersLoading;
  const productsFieldLoading = insurerFieldLoading;
  const productHasSettings = !isEmpty(selectedProduct?.credential);
  useImperativeHandle(
    ref,
    () => ({
      submit() {
        if (credentialSettingsLoading || productsFieldLoading) {
          return;
        }
        formRef.current.submitForm();
      },
      getState() {
        return {
          state: {
            credentialSettings,
            selectedProduct,
            selectedProductEntityId,
            insurerId,
            productTypes,
            connectionName,
            disabled,
            credentialSettingsLoading
          },
          form: formRef.current.values
        };
      }
    }),
    [
      connectionName,
      disabled,
      credentialSettings,
      credentialSettingsLoading,
      formRef,
      insurerId,
      productTypes,
      productsFieldLoading,
      selectedProduct,
      selectedProductEntityId
    ]
  );
  useEffect(() => {
    if (initialLoading) {
      dispatch(InsurersActions.insurers.requestData());
      setInitialLoading(false);
    }
  }, [dispatch, initialLoading]);
  useEffect(() => {
    const product = (products || []).find((p) => p["@id"] === selectedProductEntityId) ?? null;
    if (product !== selectedProduct) {
      setSelectedProduct(product);
    }
  }, [products, selectedProduct, selectedProductEntityId]);
  useEffect(() => {
    if (!selectedProduct || !productHasSettings) {
      setCredentialSettings(null);
      return;
    }
    if (!!credentialSettings && selectedProduct.credential.credentialId === credentialSettings.credentialId) {
      return;
    }
    setCredentialSettingsLoading(true);
    onboardingService.getCredentialSettings({
      credentialId: selectedProduct.credential.credentialId,
      connectionId: connection?.connectionId ?? null
    }).then((result) => {
      setCredentialSettings({
        credentialId: selectedProduct.credential.credentialId,
        data: result.data["hydra:member"]
      });
    }).catch(() => {
      return dispatch(AppActions.displayError({ method: "getCredentialSettings" }));
    }).finally(() => {
      setCredentialSettingsLoading(false);
    });
  }, [credentialSettings, dispatch, productHasSettings, selectedProduct, connection?.connectionId]);
  const changeConnectionName = useCallback(({ value: connectionName2 }) => {
    setConnectionName(connectionName2);
  }, []);
  const changeDisabled = useCallback(({ value: disabled2 }) => {
    setDisabled(disabled2);
  }, []);
  const changeInsurer = useCallback(
    ({ value: insurerId2 }) => {
      setInsurerId(insurerId2);
      setSelectedProduct(null);
      clearRelatedProducts();
    },
    [clearRelatedProducts]
  );
  const changeProduct = useCallback(
    ({ value: productEntityId }) => {
      const newProduct = (products || []).find((p) => p["@id"] === productEntityId) ?? null;
      onRelatedProductChange(newProduct);
      setSelectedProductEntityId(productEntityId);
    },
    [products, onRelatedProductChange]
  );
  const fields = useMemo(
    () => ({
      connectionName: {
        name: "connectionName",
        type: "text",
        required: true,
        header: "products.connectionName",
        placeholder: intl.formatMessage({ id: "products.connectionName" }),
        loading,
        schema: (schema) => schema.trim().strict().max(255).test("no-emojis", "No emojis allowed", (val) => {
          if (!val) return true;
          const emojiRegex = /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/;
          return !emojiRegex.test(val);
        })
      },
      insurerId: {
        name: "insurerId",
        type: "autocomplete",
        required: true,
        header: "products.selectInsurer",
        placeholder: intl.formatMessage({ id: "products.insurerName" }),
        loading: insurerFieldLoading,
        items: (insurers || []).map((insurer) => ({
          element: `${insurer.insurerName} [${insurer.type.toUpperCase()}]`,
          value: insurer.insurerId
        }))
      },
      productEntityId: {
        name: "productEntityId",
        type: "autocomplete",
        required: true,
        header: "products.selectProduct",
        placeholder: intl.formatMessage({ id: "products.productName" }),
        loading: productsFieldLoading,
        items: productsFieldLoading ? [] : (products || []).map((product) => ({
          element: product.productName,
          value: product["@id"]
        }))
      },
      disabled: {
        name: "disabled",
        type: "boolean",
        required: true,
        header: "products.disabled",
        placeholder: intl.formatMessage({ id: "products.disabled" }),
        loading
      },
      productTypes: {
        name: "productTypes",
        type: "multi-select",
        required: false,
        header: "products.selectProductTypes",
        loading: productsFieldLoading,
        items: productTypesSelectorItems
      }
    }),
    [intl, loading, insurerFieldLoading, insurers, productsFieldLoading, products, productTypesSelectorItems]
  );
  const settingsFields = useMemo(() => {
    if (!productHasSettings || isEmpty(credentialSettings)) {
      return [];
    }
    const credentialSettingsData = credentialSettings.data || [];
    return credentialSettingsData.reduce((result, settings) => {
      const currentSettings = connection?.packages?.find((p) => p.packageType === settings.packageType)?.settings || {};
      let settingsFields2 = mapSettingsField(
        intl,
        settings,
        "onboarding.settings.",
        credentialSettingsLoading,
        stepState.form,
        currentSettings,
        SettingsFieldGroup.Insurer
      );
      settingsFields2 = applyZwitserlevenSettings(settingsFields2, settings.packageType);
      return result.concat(settingsFields2);
    }, []);
  }, [
    productHasSettings,
    credentialSettings,
    connection?.packages,
    intl,
    credentialSettingsLoading,
    stepState.form,
    applyZwitserlevenSettings
  ]);
  const initialValues = useMemo(() => {
    let values = {
      connectionName,
      insurerId,
      productEntityId: isNil(selectedProduct) ? null : selectedProduct["@id"],
      productTypes,
      disabled
    };
    const credential = selectedProduct?.credential || null;
    if (credential) {
      values = setIn(values, `credentials.${credential.packageType}`, credential.credentialId);
    }
    settingsFields.forEach((field) => {
      values = setIn(values, field.name, field.value ?? field.defaultValue ?? null);
    });
    return values;
  }, [connectionName, insurerId, selectedProduct, productTypes, disabled, settingsFields]);
  const handleSubmit = useCallback(
    (values) => {
      const selectedProductIds = values.productTypes || [];
      const { productTypes: resolvedProductTypes, productsIds } = resolveRelatedProductsSubmitValues(selectedProductIds);
      const processedValues = {
        ...values,
        productTypes: resolvedProductTypes,
        productsIds
      };
      if (formRef.current) {
        Object.keys(processedValues).forEach((key) => {
          formRef.current.setFieldValue(key, processedValues[key], false);
        });
      }
      return onSubmit(processedValues);
    },
    [resolveRelatedProductsSubmitValues, onSubmit, formRef]
  );
  return /* @__PURE__ */ React.createElement(Formik, { enableReinitialize: true, initialValues, onSubmit: handleSubmit, innerRef: formRef }, ({ values }) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Grid, { container: true, spacing: 2 }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 4 }, /* @__PURE__ */ React.createElement(InputFieldFactory, { field: fields["connectionName"], onChange: changeConnectionName })), /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 4 }, /* @__PURE__ */ React.createElement(InputFieldFactory, { field: fields["insurerId"], onChange: changeInsurer })), /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 4 }, /* @__PURE__ */ React.createElement(
    InputFieldFactory,
    {
      field: fields["productEntityId"],
      onChange: changeProduct,
      disabled: !insurers || isEmpty(values.insurerId)
    }
  )), showProductTypesSelector && /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 4 }, /* @__PURE__ */ React.createElement(
    InputFieldFactory,
    {
      field: fields["productTypes"],
      onChange: changeProductTypes,
      disabled: !insurers || isEmpty(values.insurerId)
    }
  ))), /* @__PURE__ */ React.createElement(Box, { p: 1 }), /* @__PURE__ */ React.createElement(CollapsableSection, { header: "onboarding.connectionRun", hidden: false, expanded: true }, /* @__PURE__ */ React.createElement(Grid, { container: true, spacing: 2 }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 4 }, /* @__PURE__ */ React.createElement(InputFieldFactory, { field: fields["disabled"], onChange: changeDisabled })), connection && /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 4 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.connection_latestRun })), /* @__PURE__ */ React.createElement(Typography, { variant: "body1" }, formatDateTime(connectionLatestRun))), connection && /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 4 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.connection_nextRun })), /* @__PURE__ */ React.createElement(Typography, { variant: "body1" }, formatDateTime(connectionNextRun))))), /* @__PURE__ */ React.createElement(Box, { p: 1 }), /* @__PURE__ */ React.createElement(
    CollapsableSection,
    {
      header: "onboarding.insurerStep",
      loading: credentialSettingsLoading,
      expanded: !credentialSettingsLoading && !isEmpty(settingsFields),
      hidden: !productHasSettings,
      controllable: true
    },
    productHasSettings && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, { p: 1 }), /* @__PURE__ */ React.createElement(Grid, { container: true, spacing: 2 }, settingsFields.map((field, index) => /* @__PURE__ */ React.createElement(Grid, { item: true, key: index, xs: 4 }, /* @__PURE__ */ React.createElement(InputFieldFactory, { field })))))
  )));
});
ConnectionInfoStep.displayName = "ConnectionInfoStep";
export default ConnectionInfoStep;
